<template>
  <div class="paddedPage" style="overflow: auto; gap: 0;">
    <MultiColumnTable v-if="showingSystemTable" class="systemTable"
      :tableData="systemTable"
      :scrollDisabled="true"
    ></MultiColumnTable>
    <MultiColumnTable class="binTable"
      :tableName="binTableName"
      :tableData="binTable"
      :scrollDisabled="true"
    ></MultiColumnTable>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import store, { showingDesktopUi } from "./store.js";
import { combineTables, createMultiSystemTable, resolveTable } from "./TableUtility.js";
import MultiColumnTable from "./MultiColumnTable.vue";

let props = defineProps([
  "width"
]);

let route = useRoute();
let tableKey = computed(() => {
  if (route.path == "/")
    return "systemStatus";
  else
    return route.path.slice(1);
});

// The system table should be hidden on mobile, and its rows shown in the bins table. Otherwise,
// we'd have to use 'table-layout: fixed;' on both tables to ensure the row header cells have the same width
// in both tables. Using 'table-layout: fixed;' interferes with the ability of cells to expand to accommodate
// content, which is especially required on mobile.
let showingSystemTable = computed(() => {
  let connectedSessions = store.sessions.filter(session => session.isConnected);
  let allSystems = connectedSessions.map(session => session.system);
  let anySystemHasMultipleBins = allSystems.some(system => system.bins.children.length > 1);
  return (anySystemHasMultipleBins && showingDesktopUi.value);
});

let systemTable = computed(() => {
  let connectedSessions = store.sessions.filter(session => session.isConnected);
  let tables = connectedSessions.map(session => {
    // Filter bin status rows out of the table definition.
    let system = session.system;
    let tableData = JSON.parse(JSON.stringify(system.tables[tableKey.value].value));
    tableData.columns = [{ tag: system, name: system.boardName.value, subColumns: [] }]; //[tableData.columns[0]];
    tableData.rows = tableData.rows
      .filter(row => {
        let isBinRow = row.cells.some(cell => cell?.componentArgs.tag.startsWith("bins"));
        return !isBinRow;
      })
      .map(row => {
        return {
          ... row,
          cells: [row.cells[0]]
        }
      });
    return resolveTable(session, session.system, tableData);
  });
  
  // 1) Set the column widths according to the number of bins.
  // 2) Remove the bin sub-columns.
  let combinedTable = combineTables(tables, true);
  let allSystems = connectedSessions.map(session => session.system);
  let allBins = allSystems.flatMap(system => system.bins.children);
  combinedTable.columns.forEach(column => {
    let system = column.system;
    column.width = `${(system.bins.children.length / allBins.length) * 100}%`;
    column.subColumns = [];
  });
  return combinedTable;
});

let binTableName = computed(() => {
  return (showingSystemTable.value) ? "" : "Status";
});

let binTable = computed(() => {
  // Filter out the system status rows if they are being shown in the system status table.
  let showSystemRows = !showingSystemTable.value;
  let connectedSessions = store.sessions.filter(session => session.isConnected);
  let tables = connectedSessions.map(session => {
    let tableData = JSON.parse(JSON.stringify(session.system.tables[tableKey.value].value));
    if (!showSystemRows) {
      tableData.rows = tableData.rows.filter(row => {
        let isBinRow = row.cells.some(cell => cell?.componentArgs.tag.startsWith("bins"));
        return isBinRow;
      });
    }
    return resolveTable(session, session.system, tableData);
  });
  
  let showSystemColumns = !showingSystemTable.value;
  return combineTables(tables, showSystemColumns);
});
</script>

<style scoped>
.desktop .MultiColumnTable :deep(table) {
  table-layout: fixed;
}
.desktop .MultiColumnTable :deep(table) th {
  padding: 0;
}
.desktop .MultiColumnTable :deep(table) thead th:first-child {
  width: v-bind(width);
}
.systemTable {
  --table-border-bottom-style: 0;
}
</style>